// src-lit/components/product/kd-pantry-sticky-footer.ts
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { SELECTORS } from '../../constants/selectors';
import { calcStickyFooterOffset } from './pantry-sticky-footer.logic';

/**
 * @element kd-pantry-sticky-footer
 * @description えらんでKuradashi の追従フッター（合計 / 進捗バー / CTA）のラッパー。
 *   サイトフッターに重ならないよう、フッターがビューポート下端に入ってきた分だけ
 *   バーを上へ押し上げる（**非表示にはしない** = 購入導線を常に維持する）。
 *
 *   通常 PDP の <kd-sp-fixed-footer> は「購入フォームが見えたら隠す」方式だが、
 *   えらんでBOX は購入 UI がこの追従バーのみ（PC 右カラムの購入カードは描画しない）ため、
 *   隠さずに停止させる。
 *
 * @attr {string} data-stop-selector - 停止基準となる要素のセレクタ（既定: サイトフッター）
 */
@customElement('kd-pantry-sticky-footer')
export class KdPantryStickyFooter extends LitElement {
  createRenderRoot() {
    return this;
  }

  private boundOnScroll = this.onScroll.bind(this);

  private ticking = false;

  private lastOffset = -1;

  private lastSpacerHeight = -1;

  /** バー自身の高さ変化（アラート表示 / 折り返し / リサイズ）を監視してスペーサーへ反映する。 */
  private resizeObserver: ResizeObserver | null = null;

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('scroll', this.boundOnScroll, { passive: true });
    window.addEventListener('resize', this.boundOnScroll, { passive: true });

    // バーは最後まで表示され続ける（隠れない）ため、その高さ分の余白を確保しないと
    // ページ末尾のコンテンツがバーに隠れる。高さは実測してスペーサーへ反映する。
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.syncSpacerHeight());
      this.resizeObserver.observe(this);
    }
    this.syncSpacerHeight();
    this.update_();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.boundOnScroll);
    window.removeEventListener('resize', this.boundOnScroll);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    // 自分が付けた余白は必ず戻す（section 差し替え時に余白が残らないように）
    const main = document.querySelector<HTMLElement>(
      SELECTORS.PANTRY_STICKY_FOOTER.MAIN_CONTENT,
    );
    if (main && this.lastSpacerHeight >= 0) main.style.paddingBottom = '';
    this.lastSpacerHeight = -1;
  }

  /**
   * 追従バーの高さ分の余白をメインコンテンツ末尾に確保する。
   *
   * バーは最後まで表示され続ける（隠れない）ため、余白が無いとページ末尾のコンテンツ
   * （配送アコーディオン等）がバーに隠れる。セクション内に spacer を置くと本セクションより
   * **後ろ** のセクション（v2-product-content 等）を保護できないため、メインコンテナ
   * （サイトフッターを含まない）の padding-bottom として確保する。
   */
  private syncSpacerHeight(): void {
    const main = document.querySelector<HTMLElement>(
      SELECTORS.PANTRY_STICKY_FOOTER.MAIN_CONTENT,
    );
    if (!main) return;
    const height = Math.round(this.getBoundingClientRect().height);
    if (height === this.lastSpacerHeight) return;
    this.lastSpacerHeight = height;
    main.style.paddingBottom = `${height}px`;
  }

  /** scroll / resize は rAF で間引く（追従バーの再計算はレイアウト読み取りを伴うため）。 */
  private onScroll(): void {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.update_();
    });
  }

  private getStopElement(): HTMLElement | null {
    const selector = this.dataset.stopSelector;
    if (!selector) return null;
    return document.querySelector<HTMLElement>(selector);
  }

  private update_(): void {
    const stopEl = this.getStopElement();
    if (!stopEl) return;
    const offset = calcStickyFooterOffset(
      stopEl.getBoundingClientRect().top,
      window.innerHeight,
    );
    if (offset === this.lastOffset) return;
    this.lastOffset = offset;
    // fixed 要素を上へ押し上げる（非表示にはしない）
    this.style.transform = offset > 0 ? `translateY(-${offset}px)` : '';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-pantry-sticky-footer': KdPantryStickyFooter;
  }
}
